import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './useApiClient';
import { useTenantContext } from '@/shared/providers/TenantProvider';
import { getPreferences, updatePreferences } from '../api/preferencesApi';
import type { ViewSettings, PageSetting, PageSize, ColumnValueType } from '../components/viewSettings/types';

/**
 * Shared preferences hook.
 *
 * Usage A — auto-fetch on mount (e.g. page-level components):
 *   const { getPageSetting, isLoaded } = usePreferences();
 *   const { pageSize, columns } = getPageSetting('run_status');
 *
 * Usage B — on-demand fetch (e.g. ViewSettingsModal):
 *   const { data, refetch, update } = usePreferences({ autoFetch: false });
 *   // call refetch() manually to trigger the load
 */

export interface PageSettingResolved {
  pageSize: PageSize;
  columns: ColumnValueType;
}

export interface UsePreferencesOptions {
  /**
   * When true (default) the hook fetches preferences automatically on mount,
   * gated by tenant being available.
   * Set to false to preserve legacy on-demand behaviour (used by ViewSettingsModal).
   */
  autoFetch?: boolean;
}

export const usePreferences = ({ autoFetch = true }: UsePreferencesOptions = {}) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { tenant } = useTenantContext();

  // Tenant-scoped query key so different tenants never share the same cached prefs.
  const queryKey = ['preferences', tenant?.tenantId];

  const { data, refetch, isFetching, isError, isLoading, isSuccess, error } = useQuery<ViewSettings>({
    queryKey,
    queryFn: () => getPreferences(apiClient),
    // Auto-fetch: run when tenant is ready.
    // On-demand: disabled until caller invokes refetch().
    enabled: autoFetch ? !!tenant : false,
    refetchOnWindowFocus: false,
    staleTime: autoFetch
      ? 5 * 60 * 1000  // 5 min cache for auto-fetch (preferences rarely change mid-session)
      : Infinity,       // Legacy on-demand: once loaded, never auto-expire
  });

  const updateMutation = useMutation({
    mutationFn: (settings: ViewSettings) => updatePreferences(apiClient, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  /**
   * Returns the resolved PageSetting for a given page id.
   * Falls back to safe defaults when prefs are not yet loaded.
   *
   * @param pageId - The `id` field of the PageSetting (e.g. 'run_status', 'trip', 'alert')
   */
  const getPageSetting = (pageId: string): PageSetting => {
    const found = data?.pageSettings.find((p) => p.id === pageId);
    return (
      found ?? {
        id: pageId,
        page: pageId,
        pageSize: 50,
        columns: 'basic',
      }
    );
  };

  return {
    // Raw data — for callers that need the full ViewSettings (e.g. ViewSettingsModal)
    data,
    refetch,
    isFetching,
    isError,
    isLoading,
    /**
     * true once preferences have settled (succeeded or errored).
     * Use this to gate queries so the page never hangs when prefs fail.
     * Callers that need to initialise state from real server data should
     * check `isSuccess` instead of `isLoaded` for their one-time init guard.
     */
    isLoaded: isSuccess || isError,
    /**
     * true only when preferences were fetched successfully.
     * Use this for init-guard refs that must initialise from real server data
     * (so a retry-after-error can still apply the user's actual saved preference).
     */
    isSuccess,
    error,
    update: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    // Convenience helper
    getPageSetting,
  };
};